import { BaseDialog, Decorators, Fluent, PropertyGrid, PropertyGridMode, PropertyGridOptions, PropertyItem, WidgetProps, confirmDialog, faIcon, getForm } from "@serenity-is/corelib";
import { WizardDialogTexts } from "../ServerTypes/Texts";

/**
 * A dialog/panel base class that easily generates a wizard UI from tabs in a form definition (e.g. Form.cs)
 */
@Decorators.registerClass("Serenity.WizardDialog")
export abstract class WizardDialog<TEntity, P> extends BaseDialog<P> {

    declare private wizardGrid: PropertyGrid;
    declare private backButton: HTMLButtonElement;
    declare private nextButton: HTMLButtonElement;

    /**
     * Creates a new wizard dialog
     * @param opt options, might be used by derived classes
     */
    constructor(props: WidgetProps<P>) {
        super(props);

        this.element.children().forEach(x => x.classList.add("wizard-horz"));

        var tabs = this.wizardGrid.domNode.querySelector(".property-tabs.nav-underline");
        tabs?.classList.remove("nav-underline"); // for chevron look
        tabs?.classList.add("nav-tabs");

        this.wizardGrid.element.findAll(":scope > .property-tabs > li").forEach((li, i) => {
            var h = li.querySelector(':scope > a')?.innerHTML;
            Fluent(li).empty().attr('data-step', i + 1);
            li.append(<span class="badge">{i + 1}</span>)
            li.append(<span class="text" innerHTML={h}></span>);
            li.append(<span class="chevron"></span>);
        });

        this.wizardGrid.domNode.querySelectorAll(":scope > .property-tabs").forEach(el =>
            Fluent.on(el, "click", "li", (e: Event) => {
                var li = (e.target as HTMLElement)?.closest("li");
                if (!li)
                    return;

                var step = parseInt(li.dataset.step, 10);

                if (step > this.step)
                    return;

                this.moveToStep(step);
            }));

        this.wizardGrid.element.findAll(":scope > .property-panes > .tab-pane").forEach(x => x.classList.add("hidden"));

        this.wizardGrid.load(this.getInitialEntity());

        this.getStepLink(this._step).addClass("in show active");
        this.getStepPanel(this._step).addClass("in show active").removeClass("hidden");

        ["dialogbeforeclose", "panelbeforeclose", "hide.bs.modal"].forEach(x => {
            Fluent.on(this.domNode, x, e => {
                if (!this.domNode?.dataset?.confirmedclose &&
                    this.domNode?.dataset?.dialogResult != "done" &&
                    this.domNode?.dataset?.dialogResult != "save" &&
                    this.domNode?.dataset?.dialogResult != "save-and-close")
                    this.confirmCancel(e as any);
            });
        });

    }

    /**
     * gets property grid options
     */
    protected getPropertyGridOptions(): PropertyGridOptions {
        return {
            idPrefix: this.idPrefix,
            mode: PropertyGridMode.insert,
            items: this.getPropertyItems(),
            localTextPrefix: 'Forms.' + this.getFormKey() + '.'
        };
    }

    /**
     * gets form key to use to load property items / tabs
     */
    protected getFormKey(): string {
        return null;
    }

    /**
     * gets list of property items to shown on form, uses form key to load items by default
     */
    protected getPropertyItems(): PropertyItem[] {
        var formKey = this.getFormKey();
        if (formKey)
            return getForm(formKey);

        return [];
    }

    /**
     * gets initial entity to load onto form, override these to customize initial form values
     */
    protected getInitialEntity(): TEntity {
        return {} as TEntity;
    }

    // gets maximum number of steps from
    protected get maxSteps() {
        return this.wizardGrid.domNode.querySelectorAll(":scope > .property-tabs > li").length;
    }

    /**
     * moves to a step, by calling one of next, back or finish methods based on target step
     * @param toStep the target step to move to
     */
    protected moveToStep(toStep: number) {
        if (toStep == this.step || toStep < 1 || toStep > this.maxSteps + 1)
            return;

        if (toStep >= this.maxSteps + 1) {
            this.finish();
            return;
        }

        (toStep < this.step ? this.back : this.next).call(this, toStep);
    }

    private _step: number = 1;

    // gets current step
    protected get step(): number {
        return this._step;
    }

    // sets current step directly, skipping any validation
    protected set step(value: number) {
        this.getStepLink(this._step).removeClass("in active");
        this.getStepPanel(this._step).removeClass("in show active").addClass('hidden');
        this.getStepLink(value).addClass("in active");
        this.getStepPanel(value).addClass("in show active").removeClass('hidden')
            .findEach(".require-layout", x => x.trigger("layout"));
        var s: number;
        for (s = value; s <= this._step; s++)
            this.getStepLink(s).removeClass("complete").findFirst("span.badge").removeClass("badge-success");
        for (s = this._step; s < value; s++)
            this.getStepLink(s).addClass("complete").findFirst("span.badge").addClass("badge-success");

        this._step = value;

        this.backButton?.classList.toggle('disabled', this._step <= 1);
        const txt = this.nextButton?.querySelector<HTMLElement>('.txt');
        txt && (txt.textContent = WizardDialogTexts[(this._step == this.maxSteps ? "Finish" : "Next") + "Button"]);
    }

    /**
     * called to reset the form, and go back to first step
     */
    protected reset() {
        this.step = 1;
        this.wizardGrid.load(this.getInitialEntity());
    }

    /**
     * is called when user clicks the Finish button (next button on last step)
     */
    protected finish() {
        this.dialogClose("done");
    }

    /**
     * next method is called when user tries to go forward
     * @param toStep the step user is trying to move to, usually one step ahead
     */
    protected next(toStep: number) {
        this.step = toStep;
    }

    /**
     * back method is called when user tries to go backward
     * @param toStep the step user is trying to move to, usually one step back but can also be multiple
     */
    protected back(toStep: number) {
        this.step = toStep;
    }

    private getStepLink(step: number): Fluent {
        return Fluent(this.wizardGrid.domNode.querySelectorAll(":scope > .property-tabs > li").item(step - 1));
    }

    private getStepPanel(step: number): Fluent {
        return Fluent(this.wizardGrid.domNode.querySelectorAll(":scope > .property-panes > .tab-pane").item(step - 1));
    }

    protected renderContents(): any {
        const id = this.useIdPrefix();
        return (
            <div class="s-Form">
                <form id={id.Form} action="">
                    <PropertyGrid id={id.WizardGrid} class="wizard-grid" {...this.getPropertyGridOptions()} ref={w => this.wizardGrid = w} />
                    <div class="buttons" style="text-align: right;">
                        <button type="button" id={id.CancelButton} class="btn btn-danger"
                            onClick={(e) => this.confirmCancel(e)} formNoValidate>
                            <i class={faIcon("ban")}></i> {WizardDialogTexts.CancelButton}
                        </button>
                        <button type="button" id={id.BackButton} class="btn btn-primary disabled"
                            onClick={() => this.moveToStep(this.step - 1)} formNoValidate ref={w => this.backButton = w}>
                            <i class={faIcon("chevron-left")}></i> {WizardDialogTexts.BackButton}
                        </button>
                        <button type="button" id={id.NextButton} class="btn btn-primary" onClick={() => {
                            if (!this.validateForm())
                                return false;
                            this.moveToStep(this.step + 1);
                        }} formNoValidate ref={w => this.nextButton = w}>
                            <span class="txt">{WizardDialogTexts.NextButton}</span> <i class={faIcon("chevron-right")}></i>
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    /**
     * gets cancel confirmation message, return null to disable confirmation
     */
    protected getCancelMessage() {
        return WizardDialogTexts.CancelMessage;
    }

    /**
     * confirms when user tries to cancel or close the wizard
     * @param e
     */
    protected confirmCancel(e: Event) {
        var cancelMessage = this.getCancelMessage();
        if (!cancelMessage) {
            e.preventDefault();
            this.dialogClose(this.dialog.result);
            return;
        }

        e.preventDefault();
        confirmDialog(cancelMessage,
            () => {
                this.domNode.dataset.confirmedclose = "true";
                try {
                    this.dialogClose(this.dialog.result);
                }
                finally {
                    delete this.domNode.dataset.confirmedclose;
                }
            });
    }

    /** gets save entity from form, optionally from a limited list of steps (tabs)
     * @param steps the list of steps to read data from, pass null to get all
     */
    protected getSaveEntity(steps?: number[]): TEntity {
        var entity = {} as TEntity;
        this.wizardGrid.save(entity);

        if (steps != null) {
            var maxSteps = this.maxSteps;
            for (var i = 1; i <= maxSteps; i++) {
                if (steps.indexOf(i) >= 0)
                    continue;

                var panel = this.getStepPanel(i);
                panel.findEach('.field', el => {
                    var field = el.attr('class').split(' ')[1];
                    if (field)
                        delete entity[field];
                });
            }
        }

        return entity;
    }

    // gets value of compact steps option
    protected get compactSteps() {
        return !!this.domNode.querySelector(":scope > .compact-steps");
    }

    // sets value of compact steps option, when it is on, inactive step titles only show
    // numbers and gets visible on hover. this is better there are many steps to fit
    protected set compactSteps(value: boolean) {
        this.element.children().forEach(x => x.classList.add("compact-steps"));
    }
}
